// The pieces of navigation the trail (navTrail.ts, ADR 0030) is built from: the fresh-start
// marker a sidebar move carries, the history index of an entry, the scroll positions a return
// puts back, and the keys and mouse button that mean Back. Router agnostic, so the desktop app
// and the console share it.

/** A scrolled element of the page, by key ("main", or its data-nav-scroll value). */
export interface ScrollSnap {
  key: string;
  top: number;
}

/** The history state key that marks a fresh start (a sidebar click): it carries no trail. */
export const FRESH_STATE_KEY = "vdFresh";

/** The state for a sidebar move: a fresh start, so the page it opens starts a new trail. */
export function freshState(): { [FRESH_STATE_KEY]: true } {
  return { [FRESH_STATE_KEY]: true };
}

/** Whether a history state marks a fresh start. */
export function isFreshState(state: unknown): boolean {
  return (state as Record<string, unknown> | null | undefined)?.[FRESH_STATE_KEY] === true;
}

/** Elements with this attribute keep their scroll position across a jump (its value is the key). */
export const NAV_SCROLL_ATTR = "data-nav-scroll";

/** The router's history index of an entry (TanStack Router keeps it in the state). */
export function historyIndex(state: unknown): number | null {
  const i = (state as { __TSR_index?: unknown } | null | undefined)?.__TSR_index;
  return typeof i === "number" ? i : null;
}

/** The scroll positions worth keeping under `root`: the main content area and marked lists. */
export function captureScroll(root: ParentNode = document): ScrollSnap[] {
  const out: ScrollSnap[] = [];
  const main = root.querySelector("main");
  if (main && main.scrollTop > 0) out.push({ key: "main", top: Math.round(main.scrollTop) });
  for (const el of root.querySelectorAll<HTMLElement>(`[${NAV_SCROLL_ATTR}]`)) {
    const key = el.getAttribute(NAV_SCROLL_ATTR);
    if (key && el.scrollTop > 0) out.push({ key, top: Math.round(el.scrollTop) });
  }
  return out;
}

/**
 * Put the scroll positions back. Returns true once every one of them landed (its element is
 * there and tall enough), so a caller can try again while the page is still loading.
 */
export function restoreScroll(snaps: ScrollSnap[], root: ParentNode = document): boolean {
  let done = true;
  for (const s of snaps) {
    const el =
      s.key === "main"
        ? root.querySelector<HTMLElement>("main")
        : ([...root.querySelectorAll<HTMLElement>(`[${NAV_SCROLL_ATTR}]`)].find(
            (x) => x.getAttribute(NAV_SCROLL_ATTR) === s.key,
          ) ?? null);
    if (!el) {
      done = false;
      continue;
    }
    el.scrollTop = s.top;
    if (Math.abs(el.scrollTop - s.top) > 1) done = false;
  }
  return done;
}

/** Alt+Left, the keyboard's Back (never with other modifiers, never on a repeat). */
export function isBackKey(
  e: Pick<KeyboardEvent, "key" | "altKey" | "ctrlKey" | "metaKey" | "shiftKey">,
) {
  return e.key === "ArrowLeft" && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;
}

/** The mouse's Back button (the fourth button). */
export const isBackButton = (e: Pick<MouseEvent, "button">) => e.button === 3;
